"use client";

import { Paths } from "@app/routes";
import { CardDescription, CardTitle, ScrollArea } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { IHubCatalog } from "@models";
import { CatalogItemCard } from "@modules";
import { useHubCatalogAddons } from "@query";
import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router";

interface ICatalogAddonsProps {
  catalogId?: number;
}

export function CatalogAddons({ catalogId }: ICatalogAddonsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const effectiveId = catalogId ?? 0;

  const handleMoreOptions = useCallback(
    (action: string, cat?: IHubCatalog) => {
      if (
        action !== "details" ||
        cat?.catalogId == null ||
        cat.catalogId <= 0
      ) {
        return;
      }
      const params = new URLSearchParams();
      const tab = searchParams.get("tab");
      if (tab) params.set("tab", tab);
      params.set("catalogId", String(cat.catalogId));
      params.set("version", String(cat.version));
      navigate(
        `/${Paths.CatalogManager}/${Paths.CatalogDetails}?${params.toString()}`,
      );
    },
    [navigate, searchParams],
  );
  const { data: addonCatalogs = [] } = useHubCatalogAddons(effectiveId, {
    enabled: effectiveId > 0,
  });

  if (catalogId == null || addonCatalogs.length === 0) {
    return (
      <div className="flex w-full flex-col gap-2 self-start text-left">
        <CardTitle>
          {t("hub.catalog_details.supported_addons", {
            defaultValue: "Supported Addons",
          })}
        </CardTitle>
        <CardDescription className="text-foreground">
          {t("hub.catalog_details.no_addons", {
            defaultValue: "No addons for this catalog.",
          })}
        </CardDescription>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 self-start text-left">
      <CardTitle>
        {t("hub.catalog_details.supported_addons", {
          defaultValue: "Supported Addons",
        })}
      </CardTitle>
      <ScrollArea
        className="w-full pb-1"
        viewportClassName="overscroll-x-contain [-webkit-overflow-scrolling:touch]">
        <div className="grid auto-rows-auto grid-cols-[repeat(auto-fill,345px)] gap-4 justify-start">
          {addonCatalogs.map((catalog: IHubCatalog) => (
            <CatalogItemCard
              key={catalog.catalogId}
              catalog={catalog}
              onMoreOptions={handleMoreOptions}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
